import React from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { ReactComponent as FileLineIcon } from "../../../assets/images/file-line.svg";
import { ReactComponent as FileTextLineIcon } from "../../../assets/images/file-text-line.svg";
import { ReactComponent as PagesLineIcon } from "../../../assets/images/pages-line.svg";
import { ReactComponent as DeleteBinSvg } from "../../../assets/images/delete-bin-line.svg";
import styles from "./BrandVoiceContentList.module.css";

export type ContentItemType = "text" | "url" | "file";

export interface ContentItem {
    id: string;
    type: ContentItemType;
    name: string;
    content?: string; // For text items
    url?: string; // For URL items
    file?: File | { id: string; name: string }; // For file items
}

interface BrandVoiceContentListProps {
    items: ContentItem[];
    onRemoveItem: (id: string) => void;
}

const BrandVoiceContentList: React.FC<BrandVoiceContentListProps> = ({ items, onRemoveItem }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const getIcon = (type: ContentItemType) => {
        switch (type) {
            case "text":
                return <FileTextLineIcon className={styles.contentIcon} />;
            case "url":
                return <PagesLineIcon className={styles.contentIcon} />;
            case "file":
                return <FileLineIcon className={styles.contentIcon} />;
            default:
                return <FileLineIcon className={styles.contentIcon} />;
        }
    };

    const getTypeLabel = (type: ContentItemType) => {
        switch (type) {
            case "text":
                return t("brandVoices.contentType.text") || "Text";
            case "url":
                return t("brandVoices.contentType.url") || "URL";
            case "file":
                return t("brandVoices.contentType.file") || "File";
            default:
                return "";
        }
    };

    const getDisplayName = (item: ContentItem) => {
        if (item.type === "url" && item.url) {
            return item.url;
        }
        if (item.type === "file" && item.file) {
            if (item.file instanceof File) {
                return item.file.name;
            }
            return item.file.name || item.name;
        }
        return item.name;
    };

    if (items.length === 0) {
        return null;
    }

    return (
        <div className={styles.contentListContainer}>
            <div className={styles.header}>
                <h2 className={styles.title}>{t("brandVoices.contentListTitle") || "Added Content"}</h2>
            </div>
            <div className={styles.tableContainer}>
                <table className={styles.contentTable}>
                    <thead>
                        <tr>
                            <th>{t("brandVoices.contentType") || "Type"}</th>
                            <th>{t("name")}</th>
                            <th className={styles.actionsHeader}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <tr key={item.id} className={styles.contentRow}>
                                <td className={styles.typeCell}>
                                    <div className={styles.typeWrapper}>
                                        {getIcon(item.type)}
                                        <span className={styles.typeLabel}>{getTypeLabel(item.type)}</span>
                                    </div>
                                </td>
                                <td className={styles.nameCell}>
                                    <div className={styles.nameWrapper}>
                                        <span className={styles.name}>{getDisplayName(item)}</span>
                                    </div>
                                </td>
                                <td className={styles.actionsCell}>
                                    <Tooltip title={t("delete") || "Delete"} arrow>
                                        <DeleteBinSvg
                                            className={styles.deleteIcon}
                                            onClick={() => onRemoveItem(item.id)}
                                        />
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BrandVoiceContentList;
